import { rem } from "@mantine/core";
import { openContextModal } from "@mantine/modals";
import { useMyChat } from "@src/components/agent-chat/queries/chat-queries";
import { ChatPermission } from "@src/gql/graphql";
import { useMemo } from "react";
import { parseChatDocumentId } from "../collaboration/document-id";
import { LoadingSpinner } from "../loading-spinner";
import { ShareChatPanelContent } from "./share-chat-panel-content";
import type { ShareablePublication } from "./shareable-entity";

type ShareChatPanelModalProps = {
	chatId: string;
};

export function ShareChatPanelModal({ chatId }: ShareChatPanelModalProps) {
	const chatEntityId = useMemo(() => parseChatDocumentId(chatId), [chatId]);

	const { data: chat, isLoading } = useMyChat(chatEntityId.toIdString());

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-full min-h-50">
				<LoadingSpinner />
			</div>
		);
	}

	const publication: ShareablePublication | null = chat
		? {
				isPublished: Boolean(chat.isPublished),
				key: chat.isPublished ? chat.shareKey || null : null,
				isReadonly: chat.shareKeyPermission === ChatPermission.Read,
				allowClone: false,
			}
		: {
				isPublished: false,
				key: null,
				isReadonly: false,
				allowClone: false,
			};

	return (
		<ShareChatPanelContent
			chatId={chatEntityId.toString()}
			chatName={chat?.name ?? "Chat"}
			publication={publication}
		/>
	);
}

/** Above agent-chat-fullscreen overlay (`zIndex: 3500`). */
const CHAT_SHARE_MODAL_Z_INDEX = 3600;

export const openChatSharingModal = (chatId: string) => {
	openContextModal({
		modal: "buttonsModal",
		radius: "md",
		padding: rem(24),
		centered: true,
		withCloseButton: false,
		zIndex: CHAT_SHARE_MODAL_Z_INDEX,
		innerProps: {
			children: <ShareChatPanelModal chatId={chatId} />,
		},
	});
};
